import { Grid, Stack } from '@mui/material';
import { pdf, PDFViewer } from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import _ from 'lodash';
import { isEmpty } from 'lodash-es';
import { useState, useEffect, useMemo, useContext } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import {
  getAllInvoiceTypes,
  ITaxTypesDetails,
  ourEntityBankList,
  ourEntitySignatoryList,
  ClientDTO,
  IInvoiceType,
  IOurEntity,
  IOurEntityBankForm,
  IOurEntitySignatoryForm,
  getClientInvoiceInfo,
  IClientInvoice,
  IClientCurrencySettings,
  getCurrencyById,
  fileUploadToS3,
  FileDTO,
  getPresignedURLForPutInvoicePDF,
  InvoiceExtra,
  getAllInvoiceExtras,
  getAllByIds,
  getAllClientList,
  ourEntityLegalIdentifierList,
  IOurEntityLegal,
  InvoiceItem,
  ClosureDTO,
  getWorkspaceLogoForOrgEntity,
  WorkspaceLogoDTO,
  getAllTermCondition,
  getDefaultInvoiceType,
  ourEntityAllList,
  getAllActiveInvoiceCustomFields,
  FieldTypeEnum,
  getFormstackPdf,
  InvoiceTemplateDTO,
  getInvoiceTemplate,
} from 'wisestep-api';
import {
  activityFormattedDate,
  formattedDate,
  getFormatDate,
  WsButton,
  WsColors,
  WsDialogSize,
  WsSelectObj,
  WsTitleBanner,
} from 'wisestep-base';

import { useWorkspaceContext } from '../../../../src/context';
import { getFile } from '../../../features/careerPageSettings/util';
import { useDialog } from '../../../hooks/useDialog';
import { useFetch } from '../../../hooks/useFetch';
import { isBlank } from '../../../utils/StringUtils';
import { ClosureDetailsDTO } from '../../closures/types';
import { useJob } from '../../jobs/hooks';
import { WorkspaceSettingsContext } from '../WorkspaceSettingsContext';

import InvoiceForm, { closureToCandidateField } from './InvoiceForm';
import { InvoicePdf } from './invoicePreview';
import { ManageInvoiceTemplates } from './ManageInvoiceTemplates';
import { getSubtotal } from './SubTotal';

const createClosureInvoiceItem: (closure?: ClosureDTO) => InvoiceItem = (closure) => ({
  type: 'CLOSURE',
  closure,
});

const InvoiceAdd = () => {
  const location: any = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const params = useParams<string>();
  const [currentInvoice, setCurrentInvoice] = useState<IClientInvoice>();
  const [closures, setClosures] = useState<ClosureDetailsDTO[]>();
  const [invoiceExtras] = useFetch<InvoiceExtra[]>(getAllInvoiceExtras);
  const [getConversion, setConversion] = useState(false);
  const [getPlacementId, setPlacementId] = useState('');
  const { workspaceSettings } = useContext(WorkspaceSettingsContext);
  const [invoiceTypes, setInvoiceTypes] = useState<IInvoiceType[]>();
  const selectedCandidates = location.state?.selectedCandidates;
  const clientInvoiceTypeId = invoiceTypes?.find((type) => type.name === 'Invoice')?.id;
  const convertedContractPlacement = location.state?.closure as ClosureDetailsDTO | undefined;
  const [getDefaulTermConditionText, setShowTermConditionText] = useState<string>();
  const [flag, setFlag] = useState<boolean>(false);
  const [defaultInvoiceType] = useFetch(getDefaultInvoiceType);

  useEffect(() => {
    if (location.state?.selectedCandidates) {
      getAllByIds(location.state?.selectedCandidates.map((candidate) => candidate.data.id)).then(
        (response) => setClosures(response.data)
      );
      location.state?.selectedCandidates && setConversion(true);
      setPlacementId(location.state?.selectedCandidates[0].data.id);
      getAllInvoiceTypes().then(setInvoiceTypes);
    }
  }, [location.state?.selectedCandidates]);

  useEffect(() => {
    (async () => {
      const response = await getClientInvoiceInfo(params.id ?? '');
      setCurrentInvoice(response.data);
    })();
  }, [params.id]);

  useEffect(() => {
    (async () => {
      const getAllTermConditionReq = await getAllTermCondition();
      if (getAllTermConditionReq.data.length > 0) {
        setShowTermConditionText(
          getAllTermConditionReq.data[0].isDefault
            ? getAllTermConditionReq.data[0].descriptionData.des
            : 'none'
        );
      } else {
        setShowTermConditionText('none');
      }
      setFlag(true);
    })();
  }, [flag]);

  return getDefaulTermConditionText === undefined ||
    defaultInvoiceType === undefined ||
    (!isBlank(params.id) && !currentInvoice) ||
    !invoiceExtras ||
    (location.state?.selectedCandidates && (!closures || !invoiceTypes)) ||
    (convertedContractPlacement && !invoiceTypes) ? null : (
    <InvoiceAddPage
      invoiceExtras={invoiceExtras}
      getConversionStatus={getConversion}
      getPlacementValue={getPlacementId}
      defaultValues={
        currentInvoice
          ? {
              ...currentInvoice,
              items: currentInvoice.items
                ? currentInvoice.items.map((item) =>
                    item.type === 'INVOICE_EXTRA'
                      ? {
                          invoiceExtraCharge: currentInvoice.extras?.find((c) => c.id === item.id),
                        }
                      : createClosureInvoiceItem(
                          currentInvoice.closures.find((c) => c.id === item.id)
                        )
                  )
                : currentInvoice.closures.map(createClosureInvoiceItem),
              extras: currentInvoice.extras ?? [],
            }
          : {
              items: [],
              closures: [],
              ...(closures && {
                ourEntityId: closures[0].ourEntity?.id ?? null,
                clientId: closures[0].client.id,
                clientName: closures[0].client.name,
                currency: closures[0].currency,
                items: closures.map((closure) =>
                  createClosureInvoiceItem(closureToCandidateField(closure))
                ),
                subTotal: getSubtotal(
                  closures?.map((closure) =>
                    createClosureInvoiceItem(closureToCandidateField(closure))
                  ) ?? []
                ),
              }),
              descriptive: workspaceSettings.invoiceDescriptive ?? false,
              showCompensationFeeArr: workspaceSettings.invoiceShowCompensationFeeArr ?? false,
              extras: invoiceExtras
                .filter(({ active, behavior }) => active && behavior !== 'DESCRIPTION')
                .map(({ id, name, absolute }) => ({
                  id: uuidv4(),
                  absolute,
                  invoiceExtra: { id },
                  name,
                  amount: 0,
                  percent: undefined,
                })),
              taxations: [],
              adjustments: [],
              raisedDate: new Date(),
              termsAndConditions: getDefaulTermConditionText ?? '',
              ...((selectedCandidates || convertedContractPlacement) && {
                clientInvoiceTypeId,
              }),
              placementType: queryParams.get('placementType') ?? 'PERMANENT',
              lineItems: [],
              ...(convertedContractPlacement && {
                lineItems: [{ type: 'CLOSURE', contractPlacement: convertedContractPlacement }],
                clientName: convertedContractPlacement.client.name,
                clientId: convertedContractPlacement.client.id,
                ourEntityId: convertedContractPlacement.ourEntity?.id,
                currency: convertedContractPlacement.sellRateCurrency,
              }),
              ...(defaultInvoiceType?.id && { clientInvoiceTypeId: defaultInvoiceType?.id }),
            }
      }
    />
  );
};

const InvoiceAddPage = ({
  invoiceExtras,
  defaultValues,
  getConversionStatus,
  getPlacementValue,
}) => {
  const methods = useForm<any>({ defaultValues });
  const { getValues, watch } = methods;
  const tabular = !watch('descriptive');
  const showCompensationFeeArr = watch('showCompensationFeeArr');
  const template = watch('template');
  const items = watch('items');
  const [formValues, setFormValues] = useState({
    invoiceTypes: [] as WsSelectObj[],
    ourEntityList: [] as WsSelectObj[],
    ourEntityLegalList: [] as WsSelectObj[],
    signatoryDetailList: [] as WsSelectObj[],
    clientListDetailData: [] as WsSelectObj[],
    clientList: [] as WsSelectObj[],
    selectedOurEntityId: '',
    selectedClientId: '',
    bankDetailList: [] as WsSelectObj[],
  });
  const [formDataObjects, setFormDataObjects] = useState({
    invoiceTypes: [],
    ourEntityList: [],
    signatoryDetailList: [],
    clientListDetailData: [],
    bankDetailsList: [],
    selectedOurEntityId: '',
    selectedClientId: '',
    ourEntityLegalList: [],
  });
  const [openDialog, closeDialog] = useDialog();
  const [invoiceCurrency, setInvoiceCurrency] = useState<IClientCurrencySettings | undefined>(
    defaultValues.currency
  );

  const [ourEntityList, setOurEntityList] = useState<IOurEntity[]>([]);
  const formObj = watch();
  const [selectedInvoiceTaxFields, setSelectedInvoiceTaxFields] = useState<ITaxTypesDetails[]>([]);
  const [selectedAdditionalTaxFields, setSelectedAdditionalTaxFields] = useState<
    ITaxTypesDetails[]
  >([]);
  const navigate = useNavigate();
  const [fileUrl, setFileUrl] = useState<string>();
  const [job] = useJob(
    !isEmpty(items[0]?.closure?.positionId) ? items[0].closure.positionId : null
  );
  const [getDefaultBankFromOrgEntitiy, settDefaultBankFromOrgEntitiy] = useState<string>('');
  const [templates] = useFetch<InvoiceTemplateDTO[]>(getInvoiceTemplate);
  const [customFields] = useFetch(getAllActiveInvoiceCustomFields);
  const [formstackPdf, setFormstackPdf] = useState();
  const [prevValues, setPrevValues] = useState(formObj);
  const isFormstackTemplate = !!template?.formstackUrl;
  const workspace = useWorkspaceContext();

  const setListValueForForm = (response, value) =>
    response.map((data) => ({
      label: data?.[value],
      value: data?.id,
    }));
  const fetchSelectedObject = (object?: any, filterId?: string) =>
    object?.find((obj) => obj.id === filterId);

  const fetchPreviewDetails = useMemo(() => {
    const clientInvoiceType = fetchSelectedObject(
      formDataObjects.invoiceTypes,
      formObj.clientInvoiceTypeId
    ) as IInvoiceType;
    const ourEntity = fetchSelectedObject(
      formDataObjects.ourEntityList,
      formObj.ourEntityId
    ) as IOurEntity;
    const client = fetchSelectedObject(
      formDataObjects.clientListDetailData,
      formObj.clientId
    ) as ClientDTO;
    const bankDetails = fetchSelectedObject(
      formDataObjects.bankDetailsList,
      formObj.ourEntityBankId
    ) as IOurEntityBankForm;
    const ourEntitySignatoryObj = fetchSelectedObject(
      formDataObjects.signatoryDetailList,
      formObj.ourEntitySignatoryId
    ) as IOurEntitySignatoryForm;
    const legalList = formDataObjects.ourEntityLegalList as IOurEntityLegal[];
    const ourEntityLegalIdObj: IOurEntityLegal[] = [];
    if (formObj.legalIdentifierValue && !isEmpty(formObj.legalIdentifierValue)) {
      formObj.legalIdentifierValue.forEach((legalIdentifier) => {
        const tempLegalValue = legalList?.find(
          (obj) => obj.value === legalIdentifier,
          (obj) => obj.legalIdentifierName === formObj.legalIdentifierName
        );

        tempLegalValue && ourEntityLegalIdObj.push(tempLegalValue);
      });
    }

    const invoiceExtras = formObj.extras;
    return {
      clientInvoiceTypeName: clientInvoiceType?.name,
      ourEntityId: ourEntity?.id,
      ourEntityName: ourEntity?.Name,
      ourEntityLegalName: ourEntity?.Legal_Name,
      ourEntityLegalIdentifier: ourEntityLegalIdObj,
      ourEntityAddress: ourEntity?.Registered_address,
      ourEntityCorporateAddress: ourEntity?.Corporate_address,
      ourEntityLogo: ourEntity?.logo,
      clientName: client?.name,
      clientAddress: client?.address,
      clientLegalName: client?.legalName,
      bankName: bankDetails?.bankName,
      bankCode: bankDetails?.bankCode,
      bankSwiftCode: bankDetails?.swiftCode,
      bankBranch: bankDetails?.branchName,
      bankAccountNumber: bankDetails?.bankAccountNumber,
      bankIban: bankDetails?.iban,
      currency: bankDetails?.currency,
      ourEntitySignatory: ourEntitySignatoryObj?.firstname + ourEntitySignatoryObj?.lastname,
      invoiceExtras,
    };
  }, [
    formDataObjects,
    formObj.clientInvoiceTypeId,
    formObj.ourEntityId,
    formObj.clientId,
    formObj.ourEntityBankId,
    formObj.ourEntitySignatoryId,
    formObj.extras,
    formObj.legalIdentifierValue,
    formObj.legalIdentifierName,
  ]);

  useEffect(() => {
    (async () => {
      const response = await getAllInvoiceTypes();
      setFormValues((prev) => ({
        ...prev,
        invoiceTypes: setListValueForForm(response, 'name'),
      }));
      setFormDataObjects((prev) => ({
        ...prev,
        invoiceTypes: response,
      }));
    })();

    (async () => {
      const response = await getAllClientList();
      setFormValues((prev) => ({
        ...prev,
        clientList: setListValueForForm(response.data.data, 'name'),
      }));
      setFormDataObjects((prev) => ({
        ...prev,
        clientListDetailData: response.data.data,
      }));
    })();

    if (defaultValues?.currencyEntityId) {
      (async () => {
        const response = await getCurrencyById(defaultValues.currencyEntityId ?? '');
        setInvoiceCurrency(response.data);
      })();
    }
  }, [defaultValues.currencyEntityId]);
  useEffect(() => {
    if (formObj.clientId) {
      const clientId = formObj.clientId;
      (async () => {
        const response = await ourEntityAllList();
        setOurEntityList(response.data.data);
      })();
      setFormValues((prev) => ({
        ...prev,
        selectedClientId: clientId,
      }));
    }
  }, [formObj.clientId]);
  useEffect(() => {
    if (formObj.ourEntityId) {
      const ourEntityId = formObj.ourEntityId;
      (async () => {
        const response = await ourEntityBankList(ourEntityId, 0, 100);
        const object_id = response.data.data.find((el) => el.isDefault === true);

        settDefaultBankFromOrgEntitiy(object_id?.[0]?.id ?? '');

        setFormValues((prev) => ({
          ...prev,
          bankDetailList: setListValueForForm(response.data.data, 'bankName'),
        }));
        setFormDataObjects((prev) => ({
          ...prev,
          bankDetailsList: response.data.data,
        }));
      })();

      (async () => {
        const response = await ourEntityLegalIdentifierList(ourEntityId, 0, 100);
        setFormValues((prev) => ({
          ...prev,
          ourEntityLegalList: response.data.data.map((data) => ({
            label: data?.value,
            value: data?.id,
          })),
        }));

        setFormDataObjects((prev) => ({
          ...prev,
          ourEntityLegalList: response.data.data,
        }));
      })();

      (async () => {
        const response = await ourEntitySignatoryList(ourEntityId, 0, 100);
        setFormValues((prev) => ({
          ...prev,
          signatoryDetailList: setListValueForForm(response.data.data, 'firstname'),
        }));
        setFormDataObjects((prev) => ({
          ...prev,
          signatoryDetailList: response.data.data,
        }));
      })();
      setFormValues((prev) => ({
        ...prev,
        selectedOurEntityId: ourEntityId,
      }));
    }
  }, [formObj.ourEntityId]);

  useEffect(() => {
    const fetchLogoUrl = async () => {
      if (fetchPreviewDetails?.ourEntityId) {
        const logoResponse = await getWorkspaceLogoForOrgEntity(
          fetchPreviewDetails?.ourEntityId,
          'LOGO'
        );
        if (logoResponse.data) {
          const logoDetails: WorkspaceLogoDTO = logoResponse.data;
          const fileUrl = await getFile(logoDetails.s3Url.split('?')[0]);
          setFileUrl(fileUrl);
        }
      } else {
        setFileUrl('');
      }
    };
    fetchLogoUrl();
  }, [fetchPreviewDetails?.ourEntityId]);

  const PdfComponent = () => (
    <InvoicePdf
      fileUrl={fileUrl}
      fetchPreviewDetails={fetchPreviewDetails}
      raisedDate={formObj.raisedDate ? formattedDate(formObj.raisedDate.toString()) : ''}
      invoiceCode={formObj.invoiceCode}
      dueDate={formObj.dueDate ? formattedDate(formObj.dueDate.toString()) : ''}
      candidateList={formObj.closures ?? []}
      total={formObj.total}
      amountInWord={formObj.amountInWord}
      description={formObj.description}
      subTotal={formObj.subTotal}
      totalBeforeTaxation={formObj.totalBeforeTaxation}
      selectedInvoiceTaxFields={selectedInvoiceTaxFields}
      taxation={formObj.taxation}
      selectedAdditionalTaxFields={selectedAdditionalTaxFields}
      adjustment={formObj.adjustment}
      termsAndConditions={formObj.termsAndConditions}
      invoiceCurrency={invoiceCurrency}
      invoiceExtras={invoiceExtras.filter((ie) => ie.behavior !== 'DESCRIPTION')}
      discount={formObj.discount}
      discountToggle={formObj.toggled}
      tabular={tabular}
      showCompensationFeeArr={showCompensationFeeArr}
      template={template}
      items={items}
      workspaceId={workspace.id}
      jobTitle={job?.title ?? ''}
      customFields={formObj.customFields}
      lineItems={formObj.lineItems}
      showSellRateAndUnits={formObj.showSellRateAndUnits}
      placementType={formObj.placementType}
    />
  );

  const saveInvoicePDF = async (invoice: IClientInvoice): Promise<FileDTO> => {
    const blob = !isFormstackTemplate ? await pdf(<PdfComponent />).toBlob() : formstackPdf!;
    const file = new File([blob], (invoice.invoiceCode ?? 'invoice') + '.pdf', {
      type: 'application/pdf',
    });
    const response = await getPresignedURLForPutInvoicePDF(
      { fileName: file.name, fileType: file.type },
      invoice.id
    );
    await fileUploadToS3(file, response.data.preSignedUrl);
    return {
      ...invoice.pdfFile,
      name: file.name,
      location: response.data.preSignedUrl.split('?')[0],
      s3Key: response.data.fileName,
    };
  };

  const download = async (invoiceCode?: string) => {
    const blob = !isFormstackTemplate ? await pdf(<PdfComponent />).toBlob() : formstackPdf;
    saveAs(blob, invoiceCode ?? 'invoice.pdf');
  };

  useEffect(() => {
    (async () => {
      if (template?.formstackUrl) {
        const preparedInvoice = { ...prevValues };
        preparedInvoice.extras = preparedInvoice.extras.concat(
          preparedInvoice.items
            .filter((item) => item.type === 'INVOICE_EXTRA')
            .map((item) => item.invoiceExtraCharge)
        );
        preparedInvoice.closureIds = preparedInvoice.items
          .filter((item) => item.type !== 'INVOICE_EXTRA')
          .map(({ closure: { id } }) => id);
        preparedInvoice.items = (preparedInvoice as IClientInvoice).items.map((item) => ({
          type: item.type,
          id: item.type === 'INVOICE_EXTRA' ? item.invoiceExtraCharge?.id : item.closure?.id,
        }));
        preparedInvoice.currencyEntityId =
          invoiceCurrency?.id ?? preparedInvoice.closures[0]?.closure.currencyId;
        preparedInvoice.legalIdentifierIds = fetchPreviewDetails.ourEntityLegalIdentifier.map(
          (li) => li.id
        );
        preparedInvoice.customFieldList = customFields
          ?.map((field) => {
            const value = getValues('customFields')?.[field.name];
            const text =
              field.type === FieldTypeEnum.Location && value
                ? [value.city, value.state, value.country].toString() || value.continent
                : field.type === FieldTypeEnum.Date && value
                ? getFormatDate(value.toString())
                : field.type === FieldTypeEnum.DateAndTime && value
                ? activityFormattedDate(value.toString())
                : field.type === FieldTypeEnum.Tags && value
                ? value.join()
                : field.type === FieldTypeEnum.Users
                ? value?.[0]
                : field.type === FieldTypeEnum.Dropdown && field.multiple
                ? value?.join(', ')
                : field.type === FieldTypeEnum.Checkbox && field.options.length === 0
                ? value
                  ? 'Yes'
                  : 'No'
                : value;
            return { name: field.label, value: text };
          })
          .filter(({ value }) => value);
        const { data } = await getFormstackPdf(preparedInvoice);
        setFormstackPdf(data);
      }
    })();
  }, [prevValues]);

  if (!_.isEqual(formObj, prevValues)) {
    setPrevValues(formObj);
  }

  useEffect(() => {
    methods.setValue(
      'template',
      templates?.find(
        (t) =>
          t.invoiceTypeIds?.includes(formObj.clientInvoiceTypeId) &&
          t.ourEntityIds?.includes(formObj.ourEntityId) &&
          t.placementType === formObj.placementType
      )
    );
  }, [formObj.ourEntityId, formObj.clientInvoiceTypeId, templates]);

  return (
    <Grid container sx={{ display: 'flex', py: 1 }}>
      <Grid item xs={12}>
        <WsTitleBanner
          title={'Invoices'}
          goBack={() => navigate(`/invoice/list`)}
          rightChildren={
            workspace.id === 'c982f84c-d9a8-492a-906b-34a2b1831c3e' && (
              <Stack direction="row" spacing={2}>
                <WsButton
                  onClick={() => {
                    openDialog({
                      children: (
                        <ManageInvoiceTemplates
                          selectable={true}
                          onSelect={(template) => {
                            methods.setValue('template', template);
                            closeDialog();
                          }}
                        />
                      ),
                      size: WsDialogSize.LARGE,
                    });
                  }}
                >
                  Change template
                </WsButton>
                <WsButton onClick={() => navigate('/invoice/template')}>Manage templates</WsButton>
              </Stack>
            )
          }
        />
      </Grid>
      <Grid item xs={12}>
        <FormProvider {...methods}>
          <Grid container mt={3} display="flex" justifyContent="space-between">
            <Grid xs={5.8} item sx={{ maxHeight: '740px', overflow: 'scroll' }}>
              {!template?.formstackUrl ? (
                <PDFViewer height={1000} width={700}>
                  <PdfComponent />
                </PDFViewer>
              ) : (
                formstackPdf && (
                  <object
                    style={{ height: '85vh' }}
                    data={window.URL.createObjectURL(formstackPdf)}
                    type="application/pdf"
                    width="100%"
                    height="100%"
                  >
                    alt
                  </object>
                )
              )}
            </Grid>
            <Grid
              xs={5.8}
              item
              bgcolor={WsColors.White}
              sx={{ maxHeight: '740px', overflow: 'scroll' }}
            >
              <InvoiceForm
                {...formValues}
                clientListDetailData={formDataObjects.clientListDetailData}
                setSelectedInvoiceTaxFields={setSelectedInvoiceTaxFields}
                selectedInvoiceTaxFields={selectedInvoiceTaxFields}
                selectedAdditionalTaxFields={selectedAdditionalTaxFields}
                setSelectedAdditionalTaxFields={setSelectedAdditionalTaxFields}
                downloadInvoice={download}
                currentInvoice={defaultValues}
                invoiceCurrency={invoiceCurrency}
                setInvoiceCurrency={setInvoiceCurrency}
                setFormValues={setFormValues}
                setFormDataObjects={setFormDataObjects}
                saveInvoicePDF={saveInvoicePDF}
                invoiceExtras={invoiceExtras}
                ourEntities={ourEntityList}
                isConversion={getConversionStatus}
                placementId={getPlacementValue}
                defaultBankName={getDefaultBankFromOrgEntitiy}
                ourEntityLegalIdentifier={fetchPreviewDetails.ourEntityLegalIdentifier}
                customFields={customFields}
              />
            </Grid>
          </Grid>
        </FormProvider>
      </Grid>
    </Grid>
  );
};

export default InvoiceAdd;
